import { useState, useRef } from "react";
import { Clock, CalendarClock, Inbox, AlertCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { cn } from "@/shared/utils";
import type {
  TourCard as TourCardType,
  TourCardStatus,
  TourPlaceholderState,
} from "../../types/history";
import { formatFilterLine, getFilterParts } from "../../utils/formatFilter";
import type { TourFilter } from "../../api/ToursApi";

function FilterLine({ filter }: { filter: TourFilter }) {
  const [open, setOpen] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { from, toCountries, hasOverflow, overflowText, suffix } =
    getFilterParts(filter);

  const show = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    setOpen(true);
  };

  const hide = () => {
    hideTimer.current = setTimeout(() => setOpen(false), 200);
  };

  if (!hasOverflow) {
    return <>{formatFilterLine(filter)}</>;
  }

  const routePrefix = from ? `${from} → ` : "";
  const afterRoute = suffix ? ` · ${suffix}` : "";

  return (
    <>
      {routePrefix}
      <span
        className="relative inline-block"
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        <span className="font-medium underline underline-offset-2 decoration-dotted text-primary">
          {overflowText}
        </span>
        {open && (
          <span
            className={cn(
              "absolute bottom-full left-0 mb-2 z-50",
              "rounded-md border border-border bg-popover shadow-md",
              "px-4 py-3 min-w-[180px]",
              "max-h-80 overflow-y-auto overscroll-contain",
            )}
            onMouseEnter={show}
            onMouseLeave={hide}
          >
            <ul className="space-y-1.5 text-base text-popover-foreground">
              {toCountries.map((country, i) => (
                <li key={i}>{country}</li>
              ))}
            </ul>
          </span>
        )}
      </span>
      {afterRoute}
    </>
  );
}

// Служебная карточка для пустых/нерезультативных состояний.
function StatusCard({
  icon,
  text,
  muted,
}: {
  icon: React.ReactNode;
  text: string;
  muted?: boolean;
}) {
  return (
    <Card
      className={cn(
        "flex flex-col items-center justify-center gap-2 py-8 text-center border-dashed",
        muted && "opacity-60"
      )}
    >
      <div className="text-muted-foreground">{icon}</div>
      <p className="text-sm text-muted-foreground">{text}</p>
    </Card>
  );
}

// Плейсхолдер периода без прогонов.
// «planned» — прогон ещё запланирован (сегодня, время не наступило).
// Все остальные случаи без значений показываем единой пустой карточкой.
export function TourPlaceholderCard({
  placeholder,
}: {
  placeholder: TourPlaceholderState;
}) {
  if (placeholder === "planned") {
    return (
      <StatusCard
        muted
        icon={<CalendarClock className="h-6 w-6" />}
        text="Запланировано"
      />
    );
  }

  return (
    <StatusCard icon={<Inbox className="h-6 w-6" />} text="Нет предложений" />
  );
}

const STATUS_BADGE: Record<
  TourCardStatus,
  { label: string; variant: "warning" | "success" | "danger" }
> = {
  pending: { label: "В обработке", variant: "warning" },
  success: { label: "Готово", variant: "success" },
  error: { label: "Ошибка", variant: "danger" },
};

// Карточка периода: все прогоны (разбитые бэкендом по фильтрам) собраны вместе.
// Заголовок — список фильтров (виден даже в pending). Открыть можно только success.
export function TourCard({
  card,
  onOpen,
}: {
  card: TourCardType;
  onOpen: (card: TourCardType) => void;
}) {
  const badge = STATUS_BADGE[card.status];

  if (card.status === "pending") {
    return (
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <Badge variant={badge.variant}>{badge.label}</Badge>
          <Clock className="h-4 w-4 animate-pulse text-muted-foreground" />
        </CardHeader>

        <CardContent className="space-y-1.5 pb-3">
          {card.items.map((item, i) => (
            <p key={i} className="text-sm leading-snug text-muted-foreground">
              <FilterLine filter={item.filter} />
            </p>
          ))}
          <p className="pt-1 text-xs text-muted-foreground">
            Идёт поиск предложений…
          </p>
        </CardContent>

      </Card>
    );
  }

  const isSuccess = card.status === "success";

  return (
    <Card
      className={cn(
        "group transition-all",
        isSuccess && "cursor-pointer hover:shadow-lg hover:-translate-y-0.5"
      )}
      onClick={isSuccess ? () => onOpen(card) : undefined}
    >
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <Badge variant={badge.variant}>{badge.label}</Badge>
        {card.status === "error" && (
          <AlertCircle className="h-4 w-4 text-destructive" />
        )}
      </CardHeader>

      <CardContent className="space-y-1.5 pb-3">
        {card.items.map((item, i) => (
          <p key={i} className="text-sm leading-snug text-foreground">
            <FilterLine filter={item.filter} />
          </p>
        ))}
      </CardContent>

      {isSuccess ? (
        <CardFooter className="border-t pt-3">
          <Button
            size="sm"
            className="w-full"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(card);
            }}
          >
            Подробнее
          </Button>
        </CardFooter>
      ) : (
        <CardFooter className="border-t pt-3">
          <p className="text-xs text-muted-foreground">Не удалось загрузить</p>
        </CardFooter>
      )}
    </Card>
  );
}
